import React from 'react'
import RDTDataTable from 'react-data-table-component'
import { Box, Button, Typography } from '@mui/material'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from './UI/RowActionMenu'
import { cardSx } from '../utils/depth'

/**
 * Drop-in replacement for react-data-table-component used by every listing.
 *
 * It renders the table inside one white card: the `title` (with a count badge
 * when it is a <TableHeader />) on the left and the `subHeaderComponent`
 * (usually the search bar) on the right of a single heading row, followed by
 * the table and a numbered "Previous 1 2 … 9 Next" pagination bar.
 * `headRowColor` tints the column-header row (grey by default).
 * `onBulkDelete(rows)` makes the row checkboxes work: ticking rows shows a
 * "Delete selected (n)" button that confirms, then passes the rows to it.
 */
const LINE = '#E9EBF0'
const LINE_SOFT = '#EEF0F4'
const WASH = '#F6F7FA'

// Strip the per-band borders/rounding from the shared table styles: the card
// around the table now provides the frame.
const flatten = (styles = {}, headRowColor = WASH) => {
  const override = (key, style) => ({
    ...styles[key],
    style: { ...(styles[key] && styles[key].style), ...style }
  })
  const unframed = {
    borderLeft: 'none',
    borderRight: 'none',
    borderTop: 'none',
    borderBottom: 'none',
    borderRadius: 0
  }
  return {
    ...styles,
    tableWrapper: override('tableWrapper', unframed),
    noData: override('noData', unframed),
    progress: override('progress', unframed),
    headRow: override('headRow', {
      backgroundColor: headRowColor,
      border: 'none',
      borderRadius: '10px',
      minHeight: '44px'
    }),
    headCells: override('headCells', { fontSize: '13px', fontWeight: 600 }),
    rows: {
      ...styles.rows,
      style: {
        ...(styles.rows && styles.rows.style),
        minHeight: '56px',
        fontSize: '14px',
        borderBottomStyle: 'solid',
        borderBottomWidth: '1px',
        borderBottomColor: LINE_SOFT
      }
    }
  }
}

const pageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = [1]
  const from = Math.max(2, current - 1)
  const to = Math.min(total - 1, current + 1)
  if (from > 2) pages.push('…')
  for (let p = from; p <= to; p++) pages.push(p)
  if (to < total - 1) pages.push('…')
  pages.push(total)
  return pages
}

const pageButtonSx = {
  minWidth: 36,
  height: 36,
  px: 1.5,
  borderRadius: '8px',
  border: `1px solid ${LINE}`,
  color: 'text.primary',
  fontSize: 14,
  fontWeight: 500,
  textTransform: 'none',
  bgcolor: 'common.white',
  '&:hover': { bgcolor: WASH, borderColor: LINE },
  '&.Mui-disabled': { color: 'text.disabled', borderColor: LINE }
}

const makePagination = noun =>
  function Pagination({ rowsPerPage, rowCount, currentPage, onChangePage }) {
    const { t } = useTranslation()
    const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage))
    const first = rowCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1
    const last = Math.min(currentPage * rowsPerPage, rowCount)
    const go = page => onChangePage(page, rowCount)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          pt: 2.5
        }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {t('Showing')} {first} – {last} {t('of')} {rowCount} {noun}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            sx={pageButtonSx}
            disabled={currentPage <= 1}
            onClick={() => go(currentPage - 1)}>
            {t('Previous')}
          </Button>
          {pageList(currentPage, totalPages).map((page, i) =>
            page === '…' ? (
              <Box
                key={`gap-${i}`}
                sx={{
                  minWidth: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.disabled'
                }}>
                …
              </Box>
            ) : (
              <Button
                key={page}
                onClick={() => go(page)}
                sx={
                  page === currentPage
                    ? {
                        ...pageButtonSx,
                        bgcolor: 'primary.main',
                        borderColor: 'primary.main',
                        color: 'common.white',
                        '&:hover': {
                          bgcolor: 'primary.main',
                          borderColor: 'primary.main'
                        }
                      }
                    : pageButtonSx
                }>
                {page}
              </Button>
            )
          )}
          <Button
            sx={pageButtonSx}
            disabled={currentPage >= totalPages}
            onClick={() => go(currentPage + 1)}>
            {t('Next')}
          </Button>
        </Box>
      </Box>
    )
  }

function DataTable({
  title,
  subHeader,
  subHeaderComponent,
  customStyles,
  data,
  paginationServer,
  paginationTotalRows,
  paginationComponent,
  headRowColor,
  onBulkDelete,
  ...rest
}) {
  const { t } = useTranslation()
  const [selected, setSelected] = React.useState([])
  const [clearSelection, setClearSelection] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [confirmBulk, setConfirmBulk] = React.useState(false)
  const bulkDelete = async () => {
    setDeleting(true)
    try {
      await onBulkDelete(selected)
    } finally {
      setDeleting(false)
      setConfirmBulk(false)
      setSelected([])
      setClearSelection(c => !c)
    }
  }
  const bulkButton =
    onBulkDelete && selected.length ? (
      <Button
        variant="outlined"
        color="error"
        disabled={deleting}
        startIcon={<DeleteOutlineRounded />}
        onClick={() => setConfirmBulk(true)}
        sx={{ textTransform: 'none', borderRadius: '12px', minHeight: 44 }}>
        {t('DeleteSelected')} ({selected.length})
      </Button>
    ) : null
  const toolbar =
    bulkButton || (subHeader && subHeaderComponent) ? (
      <>
        {bulkButton}
        {subHeader ? subHeaderComponent : null}
      </>
    ) : null

  const count = paginationServer ? paginationTotalRows : (data || []).length
  const isHeaderElement =
    React.isValidElement(title) && title.props && 'title' in title.props
  const heading =
    isHeaderElement && title.props.count === undefined
      ? React.cloneElement(title, { count })
      : title
  const titleText = isHeaderElement ? title.props.title : title
  // "All vendors" -> "vendors", used in "Showing 1 – 10 of 43 vendors"
  const noun =
    typeof titleText === 'string'
      ? titleText.toLowerCase().replace(/^all\s+/, '')
      : ''
  const Pagination = React.useMemo(() => makePagination(noun), [noun])

  return (
    <Box
      sx={{
        ...cardSx,
        p: { xs: 2, sm: 2.5 },
        mb: 3,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        minWidth: 0,
        // Wide tables scroll inside the card instead of pushing it off-screen
        '& .rdt_Table': { minWidth: 'max-content' }
      }}>
      {heading || toolbar ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
            '& h4': { fontSize: '18px !important' }
          }}>
          <Box sx={{ minWidth: 0 }}>{heading}</Box>
          {toolbar ? (
            <Box
              sx={{
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                '& > *:not(.MuiButtonBase-root)': {
                  m: '0 !important',
                  p: '0 !important'
                },
                '& > .MuiButtonBase-root': { m: 0 }
              }}>
              {toolbar}
            </Box>
          ) : null}
        </Box>
      ) : null}
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <RDTDataTable
          {...rest}
          selectableRows={rest.selectableRows && !!onBulkDelete}
          onSelectedRowsChange={
            onBulkDelete
              ? ({ selectedRows }) => setSelected(selectedRows)
              : rest.onSelectedRowsChange
          }
          clearSelectedRows={clearSelection}
          noHeader
          data={data}
          paginationServer={paginationServer}
          paginationTotalRows={paginationTotalRows}
          paginationComponent={paginationComponent || Pagination}
          customStyles={flatten(customStyles, headRowColor)}
        />
      </Box>
      <ConfirmDialog
        open={confirmBulk}
        title={`${t('DeleteSelected')}?`}
        message={t('ConfirmDeleteSelected', { count: selected.length })}
        confirmLabel={t('Delete')}
        cancelLabel={t('Cancel')}
        loading={deleting}
        onClose={() => setConfirmBulk(false)}
        onConfirm={bulkDelete}
      />
    </Box>
  )
}

export default DataTable
